import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CarkYonetimDurumu } from "@koclukpanel/contracts";
import { Buton, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import { carkDurumAyarla, carkYonetimDurumu } from "./api-oyun2";
import { CarkTabloDuzenle } from "./CarkTabloDuzenle";
import { useSudoKapisi, useSudoOnce } from "./SudoKapisi";

/**
 * ÇARK AYARLARI (yönetim) — F-GAM-19, oyunlaştırma dalga 2 / sürüm 7b.
 *
 * Etkin tablo sürümü, beklenen değer ve dilim tablosu okunur: asıl ağırlık/olasılık hesabı sunucudadır. Tablo
 * DEĞİŞMEZ kayıttır; "Tabloyu düzenle" yeni sürüm açar (`CarkTabloDuzenle`; önizleme yalnız gösterim, doğrulamayı
 * sunucu yapar). Yazma eylemleri: çarkı açıp kapatmak ve yeni tablo sürümü (`oyun.cark.yonet`, sudo). Kapalıyken öğrenci
 * ucu 404 döner, öğrenci ekranı çarkı göstermez; defter ve geçmiş çevirmeler korunur.
 *
 * Kimin görebileceği ve kimin açıp kapatabileceği SUNUCUDA; ekran rol adına bakmaz. `yonetebilir` yalnız anahtarın
 * çizilip çizilmeyeceğini söyler (sunucu yine de izni ve sudo'yu kendisi denetler).
 */
const KAPATMA_NOTU = "Kapalıyken öğrenciler çarkı göremez; geçmiş çevirmeler ve puanlar korunur.";
const SORGU_ANAHTARI = ["oyun-cark-yonetim"] as const;

function sayiYaz(n: number): string {
  return n.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
}

export function CarkAyarlari({ yonetebilir }: { yonetebilir: boolean }) {
  const qc = useQueryClient();
  const { hataYakala, kapi, sorguKapisi } = useSudoKapisi(
    "Çarkı açmak ya da kapatmak için şifrenizi yeniden girin.",
  );
  // Okuma da sudo ister (`oyun.cark.yonet`): pencere kapaliysa sifre ONCE sorulur, sorgu bos yere 403 atmaz.
  const { sudoHazir, onKapi } = useSudoOnce(
    "Çarkı açmak ya da kapatmak için şifrenizi yeniden girin.",
  );
  const q = useQuery({
    queryKey: SORGU_ANAHTARI,
    queryFn: carkYonetimDurumu,
    retry: false,
    enabled: sudoHazir,
  });
  /** Düzenleme formu: açıldığı andaki etkin durum (`oncekiSurum` buradan) ve yeniden açma sayacı. */
  const [duzen, setDuzen] = useState<{ durum: CarkYonetimDurumu; sayac: number } | null>(null);
  const [guncelYukleniyor, setGuncelYukleniyor] = useState(false);
  const [guncelHatasi, setGuncelHatasi] = useState(false);
  const duzenleKap = useRef<HTMLSpanElement | null>(null);
  const odakDugmeye = useRef(false);
  useEffect(() => {
    if (duzen === null && odakDugmeye.current) {
      odakDugmeye.current = false;
      duzenleKap.current?.querySelector("button")?.focus();
    }
  }, [duzen]);

  const mut = useMutation({
    mutationFn: (acik: boolean) => carkDurumAyarla(acik),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SORGU_ANAHTARI }),
    // Sudo penceresi kapalıysa şifre sorulur; şifre doğrulanınca AYNI istek kendiliğinden yinelenir.
    onError: (e, acik) => hataYakala(() => mut.mutate(acik))(e),
  });

  if (onKapi) return <>{onKapi}</>;

  if (q.isError) {
    // Okuma da sudo isteyebilir: kapı çizilmezse ekranda "şifre gerekli" yazıp yapacak bir şey bırakmazdık.
    const sudoKapisi = sorguKapisi(q.error, () => void q.refetch());
    if (sudoKapisi) return <>{sudoKapisi}</>;
    return (
      <Kart baslik="Çark" ton="hata">
        <p role="alert" className="text-hata-700 text-sm">
          Çark durumu alınamadı.
        </p>
        <div className="mt-2">
          <Buton gorunum="ikincil" onClick={() => void q.refetch()}>
            Tekrar dene
          </Buton>
        </div>
      </Kart>
    );
  }

  if (q.isPending) {
    return (
      <Kart baslik="Çark">
        <Iskelet satir={4} />
      </Kart>
    );
  }

  const d = q.data;
  /** 409 SURUM_DEGISTI sonrası: güncel etkin tabloyu okuyup formu onunla yeniden açar. */
  async function guncelHaliYukle() {
    setGuncelYukleniyor(true);
    setGuncelHatasi(false);
    try {
      const r = await q.refetch();
      if (r.data) setDuzen((e) => ({ durum: r.data, sayac: (e?.sayac ?? 0) + 1 }));
      else setGuncelHatasi(true);
    } catch {
      setGuncelHatasi(true);
    } finally {
      setGuncelYukleniyor(false);
    }
  }

  const sudoSorulacak = mut.isError && mut.error instanceof ApiHatasi && mut.error.sudoGerekliMi;

  return (
    <div className="flex flex-col gap-4">
      <Kart baslik="Çark" aciklama="Öğrencilerin günlük çarkı: açık/kapalı durumu ve etkin tablo.">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-notr-600">Durum</span>
          <Rozet ton={d.acik ? "onay" : "notr"}>{d.acik ? "Açık" : "Kapalı"}</Rozet>
          {yonetebilir && (
            <Buton
              gorunum="ikincil"
              yukleniyor={mut.isPending}
              yukleniyorMetni={d.acik ? "Kapatılıyor" : "Açılıyor"}
              onClick={() => mut.mutate(!d.acik)}
            >
              {d.acik ? "Çarkı kapat" : "Çarkı aç"}
            </Buton>
          )}
        </div>
        <p className="text-notr-600 mt-2 text-xs">{KAPATMA_NOTU}</p>
        {mut.isError && !sudoSorulacak && (
          <p role="alert" className="text-hata-700 mt-2 text-sm">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Çark durumu değiştirilemedi."}
          </p>
        )}
        {kapi}
      </Kart>

      <Kart
        baslik="Etkin çark tablosu"
        aciklama="Tablo değişikliği yeni sürümle yapılır ve hemen geçerli olur; eski çevirmeler etkilenmez."
        eylem={
          yonetebilir && duzen === null ? (
            <span ref={duzenleKap}>
              <Buton gorunum="ikincil" onClick={() => setDuzen({ durum: d, sayac: 0 })}>
                Tabloyu düzenle
              </Buton>
            </span>
          ) : undefined
        }
      >
        <dl className="mb-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
          <Ozet etiket="Tablo sürümü" deger={`Sürüm ${d.surum}`} />
          <Ozet etiket="Beklenen değer" deger={`${sayiYaz(d.beklenenDeger)} puan / çevirme`} />
          <Ozet etiket="Hak aralığı" deger={`${d.bekleme} saat`} />
        </dl>
        {d.dilimler.length === 0 ? (
          <p className="text-notr-600 text-sm">Tabloda dilim yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Çark dilimleri: puan, ağırlık ve olasılık</caption>
              <thead>
                <tr className="text-notr-600 border-notr-200 border-b">
                  <th scope="col" className="py-2 pr-3 font-medium">
                    Puan
                  </th>
                  <th scope="col" className="py-2 pr-3 text-right font-medium">
                    Ağırlık
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    Olasılık
                  </th>
                </tr>
              </thead>
              <tbody className="divide-notr-100 divide-y">
                {d.dilimler.map((s, i) => (
                  <tr key={`${i}-${s.puan}`}>
                    <th scope="row" className="text-notr-900 py-2 pr-3 font-semibold tabular-nums">
                      {s.puan}
                    </th>
                    <td className="text-notr-800 py-2 pr-3 text-right tabular-nums">
                      {s.agirlik.toLocaleString("tr-TR")}
                    </td>
                    <td className="text-notr-800 py-2 text-right tabular-nums">{s.olasilik}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Kart>

      {yonetebilir && duzen !== null && (
        <>
          <CarkTabloDuzenle
            key={duzen.sayac}
            durum={duzen.durum}
            guncelYukleniyor={guncelYukleniyor}
            onGuncelYukle={() => void guncelHaliYukle()}
            onKapat={() => {
              odakDugmeye.current = true;
              setDuzen(null);
            }}
            onKaydedildi={(yeni) => {
              qc.setQueryData(SORGU_ANAHTARI, yeni);
              void qc.invalidateQueries({ queryKey: SORGU_ANAHTARI });
              odakDugmeye.current = true;
              setDuzen(null);
            }}
          />
          {guncelHatasi && (
            <p role="alert" className="text-hata-700 text-sm">
              Güncel tablo alınamadı; tekrar deneyin.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Ozet({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-notr-600">{etiket}</dt>
      <dd className="text-notr-900 font-semibold tabular-nums">{deger}</dd>
    </div>
  );
}
